import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, Menu, X, ArrowRight, Heart } from 'lucide-react';
import { useFavourites } from '@/hooks/useFavourites';
import Marquee from 'react-fast-marquee';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { useSiteContent } from '@/hooks/useSiteContent';

const ANNOUNCE_DEFAULTS = {
  announce_1: 'FREE SHIPPING OVER $150',
  announce_2: 'NEW ARRIVALS DROP 01',
  announce_3: 'JOIN THE RAD CLUB',
};

function AnnouncementBar() {
  const items = useSiteContent(ANNOUNCE_DEFAULTS);
  const messages = [items.announce_1, items.announce_2, items.announce_3].filter(Boolean);

  return (
    <div className="bg-[#0a0a0a] text-white py-2 overflow-hidden" data-testid="announcement-bar">
      <Marquee speed={40} gradient={false}>
        {[...messages, ...messages].map((msg, i) => (
          <React.Fragment key={i}>
            <span className="text-xs tracking-[0.2em] uppercase font-manrope mx-8">{msg}</span>
            <span className="text-xs tracking-[0.2em] uppercase font-manrope mx-8">&bull;</span>
          </React.Fragment>
        ))}
      </Marquee>
    </div>
  );
}

function CartDrawer() {
  const { items, subTotal, grandTotal, isCartOpen, closeCart, removeItem, updateQuantity, loading, checkoutUrl, itemQuantity } = useCart();

  const formatPrice = (price) => {
    if (!price && price !== 0) return '$0.00';
    return `$${Number(price).toFixed(2)}`;
  };

  return (
    <Sheet open={isCartOpen} onOpenChange={(open) => !open && closeCart()}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-white border-l border-black rounded-none flex flex-col" data-testid="cart-drawer">
        <SheetHeader className="border-b border-black pb-4">
          <SheetTitle className="font-bebas text-2xl tracking-tight uppercase">Your Cart ({itemQuantity})</SheetTitle>
          <SheetDescription className="sr-only">Shopping cart contents</SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <ShoppingBag className="w-12 h-12 text-gray-300" />
            <p className="text-sm text-gray-500 tracking-wide uppercase">Your cart is empty</p>
            <Button
              onClick={closeCart}
              className="rounded-none bg-black text-white hover:bg-white hover:text-black border-2 border-black transition-colors"
              data-testid="continue-shopping-btn"
            >
              Continue Shopping
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              {items.map((item) => (
                <div key={item.id} className="flex gap-4 border-b border-gray-200 pb-4" data-testid={`cart-item-${item.id}`}>
                  <div className="w-20 h-20 bg-gray-100 flex-shrink-0 overflow-hidden">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.product_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gray-200" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold uppercase tracking-wide truncate">{item.product_name || 'Product'}</p>
                    {item.options?.length > 0 && (
                      <p className="text-xs text-gray-400 mt-0.5 uppercase tracking-wide">
                        {item.options.map(o => o.value).join(' · ')}
                      </p>
                    )}
                    <p className="text-sm mt-1">
                      {formatPrice(item.price)}
                      {item.orig_price && (
                        <span className="ml-2 text-gray-400 line-through text-xs">{formatPrice(item.orig_price)}</span>
                      )}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => item.quantity > 1 ? updateQuantity(item.id, item.quantity - 1) : removeItem(item.id)}
                        className="w-7 h-7 border border-black flex items-center justify-center text-xs hover:bg-black hover:text-white transition-colors"
                        disabled={loading}
                        data-testid={`qty-decrease-${item.id}`}
                      >
                        -
                      </button>
                      <span className="text-sm w-6 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-7 h-7 border border-black flex items-center justify-center text-xs hover:bg-black hover:text-white transition-colors"
                        disabled={loading}
                        data-testid={`qty-increase-${item.id}`}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="text-xs uppercase tracking-wider text-gray-400 hover:text-black transition-colors self-start"
                    disabled={loading}
                    data-testid={`remove-item-${item.id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="border-t border-black pt-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="uppercase tracking-wider">Subtotal</span>
                <span className="font-bold">{formatPrice(subTotal)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold">
                <span className="uppercase tracking-wider">Total</span>
                <span>{formatPrice(grandTotal || subTotal)}</span>
              </div>
              <Button
                className="w-full rounded-none bg-black text-white hover:bg-white hover:text-black border-2 border-black h-12 text-sm uppercase tracking-[0.15em] font-bold transition-colors"
                onClick={() => {
                  if (checkoutUrl) window.location.href = checkoutUrl;
                }}
                disabled={loading}
                data-testid="checkout-btn"
              >
                {loading ? 'Processing...' : 'Checkout'}
                {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

const NAV_DEFAULTS = {
  logo_text: process.env.REACT_APP_STORE_TITLE || 'RAD',
  logo_image: '',
  site_title: '',
};

function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const { toggleCart, itemQuantity } = useCart();
  const { favourites } = useFavourites();
  const location = useLocation();
  const navigate = useNavigate();
  const nav = useSiteContent(NAV_DEFAULTS);

  useEffect(() => {
    if (nav.site_title) document.title = nav.site_title;
  }, [nav.site_title]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSearchOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
    setSearchOpen(false);
    setSearchQuery('');
  };

  const navLinks = [
    { to: '/shop', label: 'Shop' },
    { to: '/collections', label: 'Collections' },
    { to: '/about', label: 'About' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-black" data-testid="main-navigation">
      <nav className="h-20 flex items-center justify-between px-6 lg:px-10 max-w-[1600px] mx-auto">
        {/* Left: Desktop Menu / Mobile Hamburger */}
        <div className="flex items-center gap-8 w-1/3">
          <button
            className="lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-xs tracking-[0.15em] uppercase font-manrope font-bold relative group transition-colors ${
                  isActive(link.to) ? 'text-black' : 'text-gray-600 hover:text-black'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}-link`}
              >
                {link.label}
                <span className={`absolute -bottom-1 left-0 h-[1px] bg-black transition-all ${
                  isActive(link.to) ? 'w-full' : 'w-0 group-hover:w-full'
                }`} />
              </Link>
            ))}
          </div>
        </div>

        {/* Center: Logo */}
        <div className="w-1/3 flex justify-center">
          <Link to="/" data-testid="nav-logo">
            {nav.logo_image ? (
              <img src={nav.logo_image} alt={nav.logo_text || 'Logo'} className="h-10 w-auto object-contain" />
            ) : (
              <h1 className="font-bebas text-5xl sm:text-6xl tracking-tighter leading-none select-none">
                {nav.logo_text || 'RAD'}
              </h1>
            )}
          </Link>
        </div>

        {/* Right: Icons */}
        <div className="w-1/3 flex items-center justify-end gap-5">
          <Link to="/contact" className="hidden sm:block" data-testid="nav-contact-link">
            <span className="text-xs tracking-[0.15em] uppercase font-manrope font-bold text-gray-600 hover:text-black transition-colors">
              Contact
            </span>
          </Link>
          <button onClick={() => setSearchOpen(v => !v)} className="hover:opacity-70 transition-opacity" data-testid="search-btn">
            {searchOpen ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}
          </button>
          <Link to="/favourites" className="relative hover:opacity-70 transition-opacity" data-testid="favourites-link">
            <Heart className="w-5 h-5" />
            {favourites.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] w-4 h-4 flex items-center justify-center font-bold">
                {favourites.length}
              </span>
            )}
          </Link>
          <button
            className="relative hover:opacity-70 transition-opacity"
            onClick={toggleCart}
            data-testid="cart-toggle-btn"
          >
            <ShoppingBag className="w-5 h-5" />
            {itemQuantity > 0 && (
              <span className="absolute -top-2 -right-2 bg-black text-white text-[10px] w-4 h-4 flex items-center justify-center font-bold">
                {itemQuantity}
              </span>
            )}
          </button>
        </div>
      </nav>

      {/* Search Overlay */}
      {searchOpen && (
        <div className="border-t border-black bg-white px-6 lg:px-10 py-4" data-testid="search-overlay">
          <form onSubmit={handleSearch} className="max-w-[1600px] mx-auto flex items-center gap-4">
            <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="flex-1 text-lg font-manrope tracking-wide outline-none bg-transparent placeholder-gray-300"
              data-testid="search-input"
            />
            {searchQuery && (
              <button type="submit" className="text-xs uppercase tracking-[0.2em] font-bold border-b border-black pb-0.5 hover:opacity-60 transition-opacity">
                Search
              </button>
            )}
          </form>
        </div>
      )}

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-black bg-white" data-testid="mobile-menu">
          <div className="flex flex-col py-6 px-6 gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm tracking-[0.15em] uppercase font-bold py-2 border-b border-gray-200"
                data-testid={`mobile-nav-${link.label.toLowerCase()}-link`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="text-sm tracking-[0.15em] uppercase font-bold py-2"
              data-testid="mobile-nav-contact-link"
            >
              Contact
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

const FOOTER_DEFAULTS = {
  footer_tagline: 'STAY RAD',
  newsletter_heading: 'Join The Club',
  newsletter_description: 'Get early access to new drops, exclusive offers, and behind-the-scenes content.',
  newsletter_success: "You're in. Welcome to the RAD Club.",
  footer_copyright: `© ${new Date().getFullYear()} RAD. All rights reserved.`,
  social_instagram: '',
  social_tiktok: '',
  social_twitter: '',
};

function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const fc = useSiteContent(FOOTER_DEFAULTS);
  const footerTagline = fc.footer_tagline;
  const newsletterHeading = fc.newsletter_heading;
  const newsletterDescription = fc.newsletter_description;
  const newsletterSuccess = fc.newsletter_success;
  const copyright = fc.footer_copyright;
  const socialLinks = { instagram: fc.social_instagram, tiktok: fc.social_tiktok, twitter: fc.social_twitter };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus('success');
        setEmail('');
      } else {
        setErrorMsg(data.error || 'Something went wrong. Please try again.');
        setStatus('error');
      }
    } catch {
      setErrorMsg('Unable to subscribe right now. Please try again later.');
      setStatus('error');
    }
  };

  return (
    <footer id="footer" className="bg-[#0a0a0a] text-white" data-testid="site-footer">
      {/* Massive Typography */}
      <div className="border-b border-white/10 py-16 px-6 lg:px-10 overflow-hidden">
        <p className="font-bebas text-[15vw] md:text-[10vw] leading-none tracking-tighter text-white/10 select-none whitespace-nowrap">
          {footerTagline}
        </p>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Newsletter */}
          <div className="md:col-span-2">
            <h3 className="font-bebas text-2xl tracking-tight uppercase mb-4">{newsletterHeading}</h3>
            <p className="text-sm text-gray-400 mb-6 tracking-wide max-w-sm">
              {newsletterDescription}
            </p>
            {status === 'success' ? (
              <p className="text-sm text-white tracking-wide" data-testid="newsletter-success">
                {newsletterSuccess}
              </p>
            ) : (
              <form onSubmit={handleSubscribe}>
                <div className="flex">
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="YOUR EMAIL"
                    required
                    className="flex-1 bg-transparent border-b border-white/30 py-3 text-xs tracking-[0.15em] uppercase placeholder:text-gray-500 focus:outline-none focus:border-white transition-colors"
                    data-testid="newsletter-email-input"
                  />
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="ml-4 text-xs tracking-[0.15em] uppercase font-bold hover:text-gray-400 transition-colors disabled:opacity-50"
                    data-testid="newsletter-submit-btn"
                  >
                    {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </div>
                {status === 'error' && (
                  <p className="mt-2 text-xs text-red-400 tracking-wide" data-testid="newsletter-error">{errorMsg}</p>
                )}
              </form>
            )}
          </div>

          {/* Links */}
          <div>
            <h4 className="text-xs tracking-[0.2em] uppercase font-bold mb-6">Navigate</h4>
            <div className="flex flex-col gap-3">
              <Link to="/shop" className="text-sm text-gray-400 hover:text-white transition-colors">Shop</Link>
              <Link to="/collections" className="text-sm text-gray-400 hover:text-white transition-colors">Collections</Link>
              <Link to="/about" className="text-sm text-gray-400 hover:text-white transition-colors">About</Link>
              <Link to="/contact" className="text-sm text-gray-400 hover:text-white transition-colors">Contact</Link>
            </div>
          </div>

          <div>
            <h4 className="text-xs tracking-[0.2em] uppercase font-bold mb-6">Info</h4>
            <div className="flex flex-col gap-3">
              <Link to="/shipping-returns" className="text-sm text-gray-400 hover:text-white transition-colors">Shipping & Returns</Link>
              <span className="text-sm text-gray-400">Size Guide</span>
              <a href="https://www.iubenda.com/privacy-policy/83109306" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-white transition-colors">Privacy Policy</a>
              <a href="https://www.iubenda.com/privacy-policy/83109306/cookie-policy" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-white transition-colors">Cookie Policy</a>
              <a href="https://www.iubenda.com/terms-and-conditions/83109306" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-white transition-colors">Terms and Conditions</a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-gray-500 tracking-wider">{copyright}</p>
          <div className="flex gap-5 items-center">
            {socialLinks.instagram && (
              <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-gray-500 hover:text-white transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                </svg>
              </a>
            )}
            {socialLinks.tiktok && (
              <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="text-gray-500 hover:text-white transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/>
                </svg>
              </a>
            )}
            {socialLinks.twitter && (
              <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" aria-label="X / Twitter" className="text-gray-500 hover:text-white transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.736-8.859L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
            )}
            {!socialLinks.instagram && !socialLinks.tiktok && !socialLinks.twitter && (
              <span className="text-xs text-gray-600 tracking-wider">Add social links in TSPM</span>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <AnnouncementBar />
      <Navigation />
      <CartDrawer />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
